import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getSpeakerById,
  getSpeakerUpcomingEvents,
  getSpeakerPastEvents,
  isFavoriteSpeaker,
  toggleFavoriteSpeaker,
  recordSpeakerView,
  getSessionId,
} from '../lib/speakerService';
import SpeakerHero from './speakers/SpeakerHero';
import SpeakerEvents from './speakers/SpeakerEvents';
import SpeakerResources from './speakers/SpeakerResources';
import SpeakerTopics from './speakers/SpeakerTopics';
import InviteSpeakerModal from './speakers/InviteSpeakerModal';

const SpeakerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [speaker, setSpeaker] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // View tracking — fires once per speaker ID
  useEffect(() => {
    if (!id) return;
    const sessionId = getSessionId();
    recordSpeakerView(id, sessionId).catch(() => {});
  }, [id]);

  // Check favorite status
  useEffect(() => {
    if (!user || !id) { setIsFavorite(false); return; }
    isFavoriteSpeaker(user.id, id).then(setIsFavorite);
  }, [user, id]);

  // Fetch speaker + events
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data, error } = await getSpeakerById(id);
        if (error) throw error;
        setSpeaker(data);

        const [upcoming, past] = await Promise.all([
          getSpeakerUpcomingEvents(id),
          getSpeakerPastEvents(id),
        ]);
        setUpcomingEvents(upcoming);
        setPastEvents(past);
      } catch (err) {
        console.error('Error fetching speaker:', err);
        setSpeaker(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleToggleFavorite = useCallback(async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setIsFavorite((prev) => !prev); // Optimistic
    const { isFavorite: newFav } = await toggleFavoriteSpeaker(user.id, id, isFavorite);
    setIsFavorite(newFav);
  }, [user, id, isFavorite, navigate]);

  const handleClickTopic = (topic) => {
    navigate(`/speakers?topic=${encodeURIComponent(topic)}`);
  };

  const handleInvite = () => {
    setShowInviteModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-32 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!speaker) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Speaker Not Found</h1>
          <p className="text-xl text-gray-600 dark:text-gray-400">The requested speaker could not be found.</p>
          <Link to="/speakers" className="mt-6 inline-flex items-center text-primary hover:text-primary-dark">
            ← Back to Speakers
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-24">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link to="/speakers" className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-primary transition-colors">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Speakers
        </Link>
      </div>

      {/* Hero */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <SpeakerHero
          speaker={speaker}
          isFavorite={isFavorite}
          onToggleFavorite={handleToggleFavorite}
          onInvite={handleInvite}
          onClickTopic={handleClickTopic}
        />
      </div>

      {/* Content sections */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {/* About */}
        {speaker.bio && (
          <div className="p-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 mb-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
              About {speaker.name?.split(' ')[0] || 'Speaker'}
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{speaker.bio}</p>
          </div>
        )}

        {/* Ministry Focus */}
        {speaker.topics && speaker.topics.length > 0 && (
          <div className="p-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 mb-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Ministry Focus</h3>
            <SpeakerTopics
              topics={speaker.topics}
              max={99}
              variant="grid"
              onClickTopic={handleClickTopic}
            />
          </div>
        )}

        {/* Home Church */}
        {speaker.church_name && (
          <div className="p-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 mb-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Home Church
            </h3>
            <p className="text-gray-700 dark:text-gray-300 font-medium">{speaker.church_name}</p>
            {(speaker.church_city || speaker.church_state) && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {[speaker.church_city, speaker.church_state].filter(Boolean).join(', ')}
              </p>
            )}
            {(speaker.website_url || speaker.website) && (
              <a
                href={speaker.website_url || speaker.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-3 text-sm text-primary hover:text-primary-dark font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                Visit Website
              </a>
            )}
          </div>
        )}

        {/* Resources & Media */}
        <SpeakerResources speaker={speaker} />
      </div>

      {/* Events */}
      <SpeakerEvents upcomingEvents={upcomingEvents} pastEvents={pastEvents} />

      {/* Invite Modal */}
      <InviteSpeakerModal
        speaker={speaker}
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
      />
    </div>
  );
};

export default SpeakerDetail;
